import type {
  ApiHealth,
  ChatMessage,
  Concept,
  GapSignal,
  Lesson,
  QuizQuestion,
  School,
  StudyMaterial,
  Subject,
  StudentUnderstandingProfile,
  Topic,
  UserAccount
} from "./types";

export const demoAccounts: UserAccount[] = [];

export const demoSchools: School[] = [];

export const demoSubjects: Subject[] = [
  {
    id: "subject-life-sciences",
    name: "Life Sciences",
    description: "Cell biology, plant systems, ecosystems, and scientific reasoning."
  },
  {
    id: "subject-mathematics",
    name: "Mathematics",
    description: "Algebra, geometry, trigonometry, number patterns, and functions."
  },
  {
    id: "subject-computer-science",
    name: "Computer Science",
    description: "Introduction to Computer Science and Programming using Python (MIT 6.0001)."
  },
  {
    id: "subject-general",
    name: "General",
    description: "General subjects and lessons"
  }
];

export const demoTopics: Topic[] = [
  {
    id: "topic-photosynthesis",
    subjectId: "subject-life-sciences",
    name: "Photosynthesis",
    description: "The process by which plants convert light energy into chemical energy.",
    prerequisiteTopicIds: [],
    understandingGoal: ""
  },
  {
    id: "topic-big-data",
    subjectId: "subject-general",
    name: "Big Data Processing",
    description: "Systems, algorithms, and applications for processing large datasets",
    prerequisiteTopicIds: [],
    understandingGoal: ""
  },
  {
    id: "topic-general",
    subjectId: "subject-general",
    name: "General Topic",
    description: "General topic for lessons without specific topic metadata",
    prerequisiteTopicIds: [],
    understandingGoal: ""
  },
  {
    id: "topic-algebra",
    subjectId: "subject-mathematics",
    name: "Algebra",
    description: "Solving equations, factorisation, exponents, and algebraic manipulation.",
    prerequisiteTopicIds: [],
    understandingGoal: ""
  },
  {
    id: "topic-geometry",
    subjectId: "subject-mathematics",
    name: "Geometry",
    description: "Angles, Pythagoras, area, volume, and geometric properties.",
    prerequisiteTopicIds: [],
    understandingGoal: ""
  },
  {
    id: "topic-trigonometry",
    subjectId: "subject-mathematics",
    name: "Trigonometry",
    description: "Trigonometric ratios, special angles, identities, and solving triangles.",
    prerequisiteTopicIds: ["topic-geometry"],
    understandingGoal: ""
  },
  {
    id: "topic-number-patterns",
    subjectId: "subject-mathematics",
    name: "Number Patterns",
    description: "Sequences, series, and the general term of patterns.",
    prerequisiteTopicIds: ["topic-algebra"],
    understandingGoal: ""
  },
  {
    id: "topic-functions-graphs",
    subjectId: "subject-mathematics",
    name: "Functions and Graphs",
    description: "Linear, quadratic functions, domain, range, and graphical interpretation.",
    prerequisiteTopicIds: ["topic-algebra"],
    understandingGoal: ""
  },
  {
    id: "topic-intro-computing",
    subjectId: "subject-computer-science",
    name: "Introduction to Computing",
    description: "Fundamentals of computers, hardware, software, and history of computing.",
    prerequisiteTopicIds: [],
    understandingGoal: ""
  },
  {
    id: "topic-programming-basics",
    subjectId: "subject-computer-science",
    name: "Programming Basics",
    description: "Variables, control structures, algorithms, and problem solving.",
    prerequisiteTopicIds: [],
    understandingGoal: ""
  },
  {
    id: "topic-data-information",
    subjectId: "subject-computer-science",
    name: "Data and Information",
    description: "Data representation, storage, databases, and information management.",
    prerequisiteTopicIds: [],
    understandingGoal: ""
  },
  {
    id: "topic-networks",
    subjectId: "subject-computer-science",
    name: "Networks",
    description: "Network types, protocols, internet, and communication technologies.",
    prerequisiteTopicIds: [],
    understandingGoal: ""
  },
  {
    id: "topic-ethics",
    subjectId: "subject-computer-science",
    name: "Social and Ethical Implications",
    description: "Digital citizenship, privacy, cybersecurity, and ethical computing.",
    prerequisiteTopicIds: [],
    understandingGoal: ""
  }
];

export const demoConcepts: Concept[] = [
  {
    id: "concept-substitution",
    topicId: "topic-algebra",
    name: "Substitution",
    description: "Substitute values into expressions and equations accurately.",
    prerequisiteConceptIds: []
  },
  {
    id: "concept-solving-equations",
    topicId: "topic-algebra",
    name: "Solving Equations",
    description: "Use inverse operations to solve equations in one variable.",
    prerequisiteConceptIds: ["concept-substitution"]
  },
  {
    id: "concept-linear-equations",
    topicId: "topic-algebra",
    name: "Linear Equations",
    description: "Solve and manipulate linear equations in one variable.",
    prerequisiteConceptIds: ["concept-substitution", "concept-solving-equations"]
  },
  {
    id: "concept-quadratics",
    topicId: "topic-algebra",
    name: "Quadratics",
    description: "Factorise and solve quadratic equations.",
    prerequisiteConceptIds: ["concept-linear-equations"]
  },
  {
    id: "concept-coordinates",
    topicId: "topic-functions-graphs",
    name: "Coordinates",
    description: "Read and plot ordered pairs on the Cartesian plane.",
    prerequisiteConceptIds: ["concept-substitution"]
  },
  {
    id: "concept-gradient",
    topicId: "topic-functions-graphs",
    name: "Gradient",
    description: "Calculate and interpret the gradient or slope of a straight line.",
    prerequisiteConceptIds: ["concept-coordinates", "concept-solving-equations"]
  },
  {
    id: "concept-intercepts",
    topicId: "topic-functions-graphs",
    name: "Intercepts",
    description: "Identify and interpret x- and y-intercepts on a graph.",
    prerequisiteConceptIds: ["concept-coordinates", "concept-substitution"]
  },
  {
    id: "concept-linear-functions",
    topicId: "topic-functions-graphs",
    name: "Linear Functions",
    description: "Graph and interpret linear functions.",
    prerequisiteConceptIds: ["concept-linear-equations", "concept-coordinates", "concept-gradient"]
  },
  {
    id: "concept-straight-line-equations",
    topicId: "topic-functions-graphs",
    name: "Straight Line Equations",
    description: "Find and use equations of straight lines from graphs, gradients, intercepts, and points.",
    prerequisiteConceptIds: ["concept-linear-equations", "concept-gradient", "concept-intercepts"]
  }
];

export const demoStudyMaterials: StudyMaterial[] = [
  {
    id: "material-photosynthesis-1",
    subjectId: "subject-life-sciences",
    topicIds: ["topic-photosynthesis"],
    title: "Life Sciences Chapter 6: Photosynthesis",
    materialType: "document",
    sourceAuthority: "approved",
    excerpt: "Introduction to the process of photosynthesis in plants.",
    citation: "Life Sciences Grade 10, Chapter 6, pages 112-118"
  },
  {
    id: "material-big-data-1",
    subjectId: "subject-general",
    topicIds: ["topic-big-data"],
    title: "Big Data Analytics - Systems, Algorithms, and Applications",
    materialType: "document",
    sourceAuthority: "approved",
    excerpt: "Comprehensive textbook on big data processing and analytics.",
    citation: "Big Data Analytics - Systems, Algorithms, and Applications"
  }
];

export const demoLessons: Lesson[] = [
  {
    id: "lesson-big-data",
    notebookId: "notebook:b8ao4t7x67mzqm83dndr",
    sourceId: "source:iveuyc1t6y1faic1hu44",
    subjectId: "subject-life-sciences",
    topicId: "topic-big-data",
    lessonDependencyIds: [],
    title: "Introduction to Big Data Processing",
    teacherName: "Dr. Williams",
    recordedAt: "2026-04-21T10:00:00Z",
    durationLabel: "60 minutes",
    audioUrl: "",
    transcript: "Big data refers to datasets that are too large or complex for traditional data processing software.",
    summary: {
      highLevelSummary: "Learn the fundamentals of big data processing systems and algorithms.",
      focusAreas: [],
      conceptBreakdown: []
    },
    sourceMaterialIds: ["material-big-data-1"],
    publicationStatus: "published",
    publishingNotes: ""
  },
  {
    id: "lesson-photosynthesis",
    notebookId: "notebook:b8ao4t7x67mzqm83dndr",
    sourceId: "source:duvjkrgm0ginjmioligr",
    subjectId: "subject-life-sciences",
    topicId: "topic-photosynthesis",
    lessonDependencyIds: [],
    title: "Introduction to Photosynthesis",
    teacherName: "Ms. Ndlovu",
    recordedAt: "2026-01-15T10:00:00Z",
    durationLabel: "45 minutes",
    audioUrl: "",
    transcript: "Photosynthesis is the process by which green plants use sunlight to synthesize foods from carbon dioxide and water.",
    summary: {
      highLevelSummary: "Learn the basics of photosynthesis and its importance for life on Earth.",
      focusAreas: [],
      conceptBreakdown: []
    },
    sourceMaterialIds: ["material-photosynthesis-1"],
    publicationStatus: "published",
    publishingNotes: ""
  }
];

export const demoQuizQuestions: QuizQuestion[] = [];

export const demoChatHistory: ChatMessage[] = [];

export const demoUnderstandingProfiles: Record<string, StudentUnderstandingProfile> = {};

export const demoApiHealth: ApiHealth[] = [
  {
    domain: "Wrapper API",
    status: "ready",
    notes: "Backend API is running."
  },
  {
    domain: "Neo4j Database",
    status: "ready",
    notes: "Graph database is connected."
  }
];

export function buildGapSignalFromQuiz(question: QuizQuestion, answer: string): GapSignal[] {
  if (answer === question.correctAnswer) {
    return [];
  }
  const topicId = question.topicId || question.prerequisiteTopicIds[0];
  if (!topicId) {
    return [];
  }
  return [
    {
      id: `demo-quiz-gap-${question.id}-${Date.now()}`,
      topicId,
      topicName: question.conceptTag || topicId,
      signalSource: "quiz",
      severity: "medium",
      confidence: 0.72,
      evidence: `Quiz miss: answered "${answer}" for item ${question.id}.`,
      recommendedAction: "Review approved materials for this topic."
    }
  ];
}

export function buildGapSignalFromChat(_message: string): GapSignal[] {
  return [];
}
